/** redux-persist migrations for the `root` key.
 *
 * The persisted blob is the WHOLE combined root ({ admin, price, user, formBuilder }).
 * createMigrate runs every key > stored version and <= configured version, so each
 * key here must match a `version` bump in src/store.js. Never add root keys: the
 * reconciler (redux-deep-persist's autoMergeDeep) copies unknown root keys through.
 */
import { MAX_ACCOUNTS } from '../constants/accounts';

/** v1 (#168): lift the single-account user slice into the multi-account shape.
 * Old: user = { token, currentUser, remember, ... }
 * New: user.accounts = [{ username, token, currentUser, remember }], user.activeUsername
 * `token`/`currentUser` stay in place as the live projection of the active account,
 * so an existing signed-in user is NOT logged out by the upgrade. */
export const migrateToMultiAccount = (state) => {
	if (!state || !state.user) return state;
	const user = state.user;
	const impersonating = Boolean(state.admin?.isImpersonating);
	const accounts = Array.isArray(user.accounts) ? user.accounts : [];
	const username = user.currentUser?.username;
	const signedIn = Boolean(user.token && username);

	// An impersonation session is never an account; leave the projection as-is and
	// let stopImpersonation clear it later.
	if (impersonating) {
		return { ...state, user: { ...user, impersonating } };
	}

	// The projection is the source of truth here. A v1 → v0 rollback leaves any
	// `accounts` list intact while v0 keeps writing only currentUser/token (and
	// version 0), so on redeploy this migration runs again against a list that
	// may no longer match: reconcile it rather than trusting it.
	if (!signedIn) {
		// Signed out under v0 → no account may survive (otherwise
		// reconcileAccounts would resurrect a stale one).
		return { ...state, user: { ...user, impersonating: false, accounts: [], activeUsername: null } };
	}

	const entry = {
		username,
		token: user.token,
		currentUser: user.currentUser,
		remember: user.remember !== false,
	};
	const idx = accounts.findIndex((a) => a.username === username); // exact case
	let nextAccounts =
		idx === -1
			? [...accounts, entry]
			: accounts.map((a, i) => (i === idx ? { ...a, token: entry.token, currentUser: entry.currentUser } : a));
	// Keep the cap: drop the oldest OTHER accounts first.
	while (nextAccounts.length > MAX_ACCOUNTS) {
		const drop = nextAccounts.findIndex((a) => a.username !== username);
		nextAccounts = nextAccounts.filter((_, i) => i !== drop);
	}

	return {
		...state,
		user: { ...user, impersonating: false, activeUsername: username, accounts: nextAccounts },
	};
};

const migrations = {
	1: migrateToMultiAccount,
};

export default migrations;
